import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { TAKE } from '@/lib/constants/pagination';
import { fetchSupportTicket, fetchSupportTickets } from './api';
import type { FetchSupportTicketsParams } from './models';

/** Radice delle chiavi: un solo prefisso per tutto il dominio ticket. */
const SUPPORT_TICKET_KEY = 'support-ticket';

export const supportTicketQueryKeys = {
  /**
   * Il tenant fa parte della chiave, non solo dell'URL: cambiando azienda i
   * ticket dell'una non devono comparire in cache sotto l'altra.
   */
  all: (tenantId?: string) => [SUPPORT_TICKET_KEY, tenantId] as const,
  lists: (tenantId?: string) =>
    [...supportTicketQueryKeys.all(tenantId), 'list'] as const,
  infinite: (tenantId: string, filters: FetchSupportTicketsParams) =>
    infiniteQueryOptions({
      queryKey: [...supportTicketQueryKeys.lists(tenantId), filters],
      queryFn: ({ pageParam = 0 }) =>
        fetchSupportTickets(tenantId, {
          ...filters,
          skip: pageParam * TAKE,
          take: TAKE,
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.data.length === 0) return undefined;
        const loadedItems = allPages.reduce(
          (sum, page) => sum + page.data.length,
          0
        );
        return loadedItems >= lastPage.totalCount ? undefined : allPages.length;
      },
    }),
  details: (tenantId?: string) =>
    [...supportTicketQueryKeys.all(tenantId), 'detail'] as const,
  /**
   * Il dettaglio non si serve mai dalla cache senza ricontrollare.
   *
   * Si arriva qui dal deep-link della notifica o della email, cioè proprio
   * quando il ticket è appena cambiato: con lo `staleTime` di default il
   * cliente riaprirebbe la scheda e leggerebbe lo stato di prima. Il thread è
   * una manciata di messaggi, la rilettura non costa nulla.
   */
  detail: (tenantId: string, ticketId: string) =>
    queryOptions({
      queryKey: [...supportTicketQueryKeys.details(tenantId), ticketId],
      queryFn: () => fetchSupportTicket(tenantId, ticketId),
      staleTime: 0,
      refetchOnMount: 'always',
    }),

  // --- [Tecnos/Gestify] Chiavi degli slot formazione. Dormienti. -----------
  slots: () => [SUPPORT_TICKET_KEY, 'slots'] as const,
  slotsDays: (tenantId: string, tipo: string, from: string, to: string) =>
    [
      ...supportTicketQueryKeys.slots(),
      'days',
      tenantId,
      tipo,
      from,
      to,
    ] as const,
  slotsHours: (tenantId: string, tipo: string, date: string) =>
    [...supportTicketQueryKeys.slots(), 'hours', tenantId, tipo, date] as const,
};

/**
 * Chiavi da rinfrescare dopo l'apertura di un ticket.
 *
 * Sta fuori dalla mutation perché la regola — la lista del tenant deve
 * ripartire, altrimenti il ticket appena aperto non compare finché non si
 * ricarica la pagina — è verificabile da sola, mentre l'hook di creazione
 * andrebbe montato con un `QueryClientProvider` per dire la stessa cosa.
 */
export const getQueryKeysToInvalidateAfterTicketCreated = (
  tenantId: string
): readonly (readonly unknown[])[] => [supportTicketQueryKeys.lists(tenantId)];
